import { useState, useMemo } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
  Row,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  AlertTriangle,
  RotateCcw,
  Ban,
  CheckCircle2,
  XCircle,
  RefreshCw,
  MapPin,
  Receipt,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DataTableColumnHeader,
  DataTablePagination,
} from '@/components/data-table'
import { useLeaseViewStore } from '@/stores/leaseViewStore'
import type {
  LeaseTableRow,
  LeaseStatus,
  LeaseViewId,
  LeaseColumnGroupId,
  LeaseGroupingOption,
} from '@/types/leases'
import {
  LEASE_STATUS_LABELS,
  LEASE_STATUS_VARIANTS,
  RENEWAL_STATUS_LABELS,
  RENEWAL_STATUS_VARIANTS,
  CONTRACT_TYPE_LABELS,
  LEASE_COLUMN_GROUPS,
  getDaysUntilExpiry,
  getDaysInDraft,
  getContractTypeFromUnit,
  formatCurrency,
  getTermTypeLabel,
  canTerminate,
  canCancel,
  canComplete,
} from '@/types/leases'

interface LeasesTableProps {
  data: LeaseTableRow[]
  isLoading: boolean
  activeView: LeaseViewId
  onEdit: (id: string) => void
  onDelete: (id: string, contractNumber: string) => void
  onRowClick: (id: string) => void
  onRestore?: (id: string) => void
  onTerminate?: (id: string, contractNumber: string) => void
  onCancel?: (id: string, contractNumber: string) => void
  onComplete?: (id: string, contractNumber: string) => void
  onStartRenewal?: (id: string) => void
  onViewOnMap?: (unitId: string) => void
}

// ============================================
// Column visibility from view + column groups
// ============================================

const COLUMN_TO_GROUP: Record<string, LeaseColumnGroupId> = {
  // Identity (always on)
  contract_number: 'identity',
  client: 'identity',
  unit: 'identity',
  aircraft: 'identity',
  term: 'identity',
  monthly_rate: 'identity',
  status: 'identity',
  // Portfolio contextual
  contract_type: 'portfolio',
  auto_renew: 'portfolio',
  // Renewal contextual
  days_to_expiry: 'renewal',
  renewal_status: 'renewal',
  last_contact: 'renewal',
  // Drafts contextual
  created_date: 'drafts',
  days_in_draft: 'drafts',
  // General contextual (All view)
  general_contract_type: 'general',
  general_term_type: 'general',
  // Financial (manual toggle only)
  security_deposit: 'financial',
}

function getColumnVisibility(visibleGroups: Set<LeaseColumnGroupId>): VisibilityState {
  const visibility: VisibilityState = { select: true, actions: true }

  for (const [colId, groupId] of Object.entries(COLUMN_TO_GROUP)) {
    const group = LEASE_COLUMN_GROUPS.find((g) => g.id === groupId)
    visibility[colId] = group?.alwaysOn || visibleGroups.has(groupId)
  }

  return visibility
}

// ============================================
// Grouping
// ============================================

function groupRows(
  rows: Row<LeaseTableRow>[],
  groupBy: LeaseGroupingOption
): Map<string, Row<LeaseTableRow>[]> {
  if (groupBy === 'none') {
    return new Map([['all', rows]])
  }

  const groups = new Map<string, Row<LeaseTableRow>[]>()

  rows.forEach((row) => {
    let groupKey: string

    switch (groupBy) {
      case 'contract_type':
        groupKey = CONTRACT_TYPE_LABELS[getContractTypeFromUnit(row.original.unit_type)]
        break
      case 'building':
        groupKey = row.original.building_name
          ? `${row.original.building_name}${row.original.unit_code ? ` / ${row.original.unit_code}` : ''}`
          : 'Unassigned'
        break
      case 'client':
        groupKey = row.original.client_name || 'Unknown'
        break
      case 'renewal_status':
        groupKey = row.original.renewal_status
          ? RENEWAL_STATUS_LABELS[row.original.renewal_status]
          : 'No Status'
        break
      case 'location':
        groupKey = row.original.location_name || 'Unknown Location'
        break
      default:
        groupKey = 'all'
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)!.push(row)
  })

  return groups
}

// ============================================
// Term display helper
// ============================================

function formatTermRange(lease: LeaseTableRow): string {
  if (lease.type === 'month_to_month' || !lease.end_date) return 'M2M'
  const fmt = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${fmt(lease.start_date)} → ${fmt(lease.end_date)}`
}

// ============================================
// Table Component
// ============================================

export function LeasesTable({
  data,
  isLoading,
    onEdit,
  onDelete,
  onRowClick,
  onRestore,
  onTerminate,
  onCancel,
  onComplete,
  onStartRenewal,
  onViewOnMap,
}: LeasesTableProps) {
  const {
    groupBy,
    visibleColumnGroups,
    search,
    setSearch,
  } = useLeaseViewStore()

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const columnVisibility = useMemo(
    () => getColumnVisibility(visibleColumnGroups),
    [visibleColumnGroups]
  )

  const columns: ColumnDef<LeaseTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    // ---- Always-on core columns (identity group) ----
    {
      accessorKey: 'contract_number',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Contract #" />,
      cell: ({ row }) => {
        const isDeleted = !!row.original.deleted_at
        const daysToExpiry = getDaysUntilExpiry(row.original.end_date)
        const showFlag =
          !isDeleted &&
          daysToExpiry !== null &&
          daysToExpiry >= 0 &&
          daysToExpiry <= 90 &&
          row.original.renewal_status === 'not_started'

        return (
          <div>
            <span className={cn('font-mono font-medium', isDeleted && 'line-through text-muted-foreground')}>
              {row.getValue('contract_number')}
            </span>
            {showFlag && (
              <div className="flex items-center gap-1 mt-0.5">
                <AlertTriangle className="h-3 w-3 text-orange-500" />
                <span className="text-xs text-orange-600 dark:text-orange-500">
                  Expires in {daysToExpiry}d
                </span>
              </div>
            )}
          </div>
        )
      },
      enableHiding: false,
    },
    {
      id: 'client',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Client" />,
      cell: ({ row }) => {
        const clientName = row.original.client_name
        return clientName ? (
          <span className="font-medium">{clientName}</span>
        ) : (
          <span className="text-muted-foreground">&mdash;</span>
        )
      },
    },
    {
      id: 'unit',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Unit" />,
      cell: ({ row }) => {
        const unitCode = row.original.unit_code
        const buildingName = row.original.building_name

        if (unitCode) {
          return (
            <div>
              <div className="font-medium">{unitCode}</div>
              {buildingName && (
                <div className="text-xs text-muted-foreground">{buildingName}</div>
              )}
            </div>
          )
        }

        return <span className="text-muted-foreground">&mdash;</span>
      },
    },
    {
      id: 'aircraft',
      header: 'Aircraft',
      cell: ({ row }) => {
        const tail = row.original.primary_aircraft_tail
        const type = row.original.primary_aircraft_type
        if (!tail) return <span className="text-muted-foreground">&mdash;</span>
        return (
          <div className="text-sm">
            <span className="font-mono font-medium">{tail}</span>
            {type && (
              <span className="text-muted-foreground ml-1.5">&middot; {type}</span>
            )}
          </div>
        )
      },
    },
    {
      id: 'term',
      header: 'Term',
      cell: ({ row }) => {
        const label = formatTermRange(row.original)
        return <span className="text-sm whitespace-nowrap">{label}</span>
      },
      sortingFn: (a, b) => {
        return new Date(a.original.start_date).getTime() - new Date(b.original.start_date).getTime()
      },
    },
    {
      accessorKey: 'monthly_rate',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Monthly Rate" />,
      cell: ({ row }) => (
        <span className="font-medium">{formatCurrency(row.getValue('monthly_rate') as number)}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.getValue('status') as LeaseStatus
        return (
          <Badge variant={LEASE_STATUS_VARIANTS[status]}>
            {LEASE_STATUS_LABELS[status]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    // ---- Portfolio contextual columns ----
    {
      id: 'contract_type',
      header: 'Type',
      cell: ({ row }) => {
        const contractType = getContractTypeFromUnit(row.original.unit_type)
        return (
          <Badge variant="outline" className="text-xs">
            {CONTRACT_TYPE_LABELS[contractType]}
          </Badge>
        )
      },
    },
    {
      id: 'auto_renew',
      header: 'Auto-Renew',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.auto_renew ? 'Yes' : 'No'}</span>
      ),
    },
    // ---- Renewal contextual columns ----
    {
      id: 'days_to_expiry',
      header: 'Days to Expiry',
      cell: ({ row }) => {
        const days = getDaysUntilExpiry(row.original.end_date)
        if (days === null) return <span className="text-muted-foreground">&mdash;</span>

        let colorClass = ''
        if (days < 0) colorClass = 'text-destructive font-medium'
        else if (days <= 30) colorClass = 'text-destructive font-medium'
        else if (days <= 60) colorClass = 'text-orange-600 dark:text-orange-500 font-medium'
        else if (days <= 90) colorClass = 'text-yellow-600 dark:text-yellow-500'

        return <span className={`text-sm ${colorClass}`}>{days}d</span>
      },
      sortingFn: (a, b) => {
        const aDays = getDaysUntilExpiry(a.original.end_date) ?? 99999
        const bDays = getDaysUntilExpiry(b.original.end_date) ?? 99999
        return aDays - bDays
      },
    },
    {
      id: 'renewal_status',
      header: 'Renewal',
      cell: ({ row }) => {
        const rs = row.original.renewal_status
        if (!rs) return <span className="text-muted-foreground">&mdash;</span>

        return (
          <Badge variant={RENEWAL_STATUS_VARIANTS[rs]} className="text-xs">
            {RENEWAL_STATUS_LABELS[rs]}
          </Badge>
        )
      },
    },
    {
      id: 'last_contact',
      header: 'Last Contact',
      cell: ({ row }) => {
        const date = row.original.renewal_contacted_date
        if (!date) return <span className="text-muted-foreground">&mdash;</span>
        return (
          <span className="text-sm text-muted-foreground">
            {new Date(date).toLocaleDateString()}
          </span>
        )
      },
    },
    // ---- Drafts contextual columns ----
    {
      id: 'created_date',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.created_at).toLocaleDateString()}
        </span>
      ),
      sortingFn: (a, b) => {
        return new Date(a.original.created_at).getTime() - new Date(b.original.created_at).getTime()
      },
    },
    {
      id: 'days_in_draft',
      header: 'Days in Draft',
      cell: ({ row }) => {
        const days = getDaysInDraft(row.original.created_at)
        const colorClass = days > 14 ? 'text-orange-600 dark:text-orange-500 font-medium' :
          days > 7 ? 'text-yellow-600 dark:text-yellow-500' : ''
        return <span className={`text-sm ${colorClass}`}>{days}d</span>
      },
      sortingFn: (a, b) => {
        return getDaysInDraft(b.original.created_at) - getDaysInDraft(a.original.created_at)
      },
    },
    // ---- General contextual columns (All view) ----
    {
      id: 'general_contract_type',
      header: 'Contract Type',
      cell: ({ row }) => {
        const contractType = getContractTypeFromUnit(row.original.unit_type)
        return (
          <Badge variant="outline" className="text-xs">
            {CONTRACT_TYPE_LABELS[contractType]}
          </Badge>
        )
      },
    },
    {
      id: 'general_term_type',
      header: 'Term Type',
      cell: ({ row }) => (
        <span className="text-sm">{getTermTypeLabel(row.original)}</span>
      ),
    },
    // ---- Financial (manual toggle) ----
    {
      id: 'security_deposit',
      accessorKey: 'security_deposit',
      header: 'Deposit',
      cell: ({ row }) => {
        const deposit = row.original.security_deposit
        return deposit != null ? (
          <span className="text-sm">{formatCurrency(deposit)}</span>
        ) : (
          <span className="text-muted-foreground">&mdash;</span>
        )
      },
    },
    // ---- Actions ----
    {
      id: 'actions',
      cell: ({ row }) => {
        const isDeleted = !!row.original.deleted_at

        if (isDeleted) {
          return (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                onRestore?.(row.original.id)
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Restore
            </Button>
          )
        }

        const isExpiring = row.original.status === 'expiring' ||
          (row.original.status === 'active' && getDaysUntilExpiry(row.original.end_date) !== null &&
            getDaysUntilExpiry(row.original.end_date)! <= 90 && getDaysUntilExpiry(row.original.end_date)! >= 0)

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              {onStartRenewal && isExpiring && (
                <DropdownMenuItem onClick={() => onStartRenewal(row.original.id)}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Start Renewal
                </DropdownMenuItem>
              )}
              {onViewOnMap && row.original.unit_id && (
                <DropdownMenuItem onClick={() => onViewOnMap(row.original.unit_id)}>
                  <MapPin className="mr-2 h-4 w-4" />
                  View on Map
                </DropdownMenuItem>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem disabled className="opacity-50">
                    <Receipt className="mr-2 h-4 w-4" />
                    Create Invoice
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent side="left">Invoicing coming soon</TooltipContent>
              </Tooltip>
              <DropdownMenuSeparator />
              {onComplete && canComplete(row.original.status) && (
                <DropdownMenuItem onClick={() => onComplete(row.original.id, row.original.contract_number)}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Complete
                </DropdownMenuItem>
              )}
              {onTerminate && canTerminate(row.original.status) && (
                <DropdownMenuItem
                  onClick={() => onTerminate(row.original.id, row.original.contract_number)}
                  className="text-destructive focus:text-destructive"
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Terminate
                </DropdownMenuItem>
              )}
              {onCancel && canCancel(row.original.status) && (
                <DropdownMenuItem onClick={() => onCancel(row.original.id, row.original.contract_number)}>
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDelete(row.original.id, row.original.contract_number)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const s = filterValue.toLowerCase()
      const contractNumber = (row.original.contract_number || '').toLowerCase()
      const clientName = (row.original.client_name || '').toLowerCase()
      const unitCode = (row.original.unit_code || '').toLowerCase()
      const buildingName = (row.original.building_name || '').toLowerCase()
      const tailNumber = (row.original.primary_aircraft_tail || '').toLowerCase()

      return (
        contractNumber.includes(s) ||
        clientName.includes(s) ||
        unitCode.includes(s) ||
        buildingName.includes(s) ||
        tailNumber.includes(s)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: search,
    },
    initialState: {
      pagination: { pageSize: 15 },
    },
  })

  // Group rows by selected option
  const groupedRows = useMemo(() => {
    const filteredRows = table.getRowModel().rows
    return groupRows(filteredRows, groupBy)
  }, [table.getRowModel().rows, groupBy])

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">Loading lease contracts...</div>
                </TableCell>
              </TableRow>
            ) : groupedRows.size === 0 || (groupedRows.size === 1 && groupedRows.get('all')?.length === 0) ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">
                    No lease contracts found. Add a lease to get started.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              Array.from(groupedRows.entries()).map(([groupKey, rows]) => (
                <GroupSection
                  key={groupKey}
                  groupKey={groupKey}
                  groupBy={groupBy}
                  rows={rows}
                  colSpan={table.getVisibleFlatColumns().length}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </div>
  )
}

// ============================================
// Group Section
// ============================================

function GroupSection({
  groupKey,
  groupBy,
  rows,
  colSpan,
  onRowClick,
}: {
  groupKey: string
  groupBy: LeaseGroupingOption
  rows: Row<LeaseTableRow>[]
  colSpan: number
  onRowClick: (id: string) => void
}) {
  if (groupBy === 'none' || groupKey === 'all') {
    return (
      <>
        {rows.map((row) => {
          const isDeleted = !!row.original.deleted_at
          return (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && 'selected'}
              className={cn('cursor-pointer', isDeleted && 'opacity-50')}
              onClick={() => onRowClick(row.original.id)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </>
    )
  }

  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={colSpan} className="py-2">
          <div className="flex items-center gap-2 font-medium">
            <span>{groupKey}</span>
            <span className="text-muted-foreground font-normal">({rows.length})</span>
          </div>
        </TableCell>
      </TableRow>
      {rows.map((row) => {
        const isDeleted = !!row.original.deleted_at
        return (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            className={cn('cursor-pointer', isDeleted && 'opacity-50')}
            onClick={() => onRowClick(row.original.id)}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        )
      })}
    </>
  )
}
